/**
 * Launch Status Badge Component
 *
 * Shared component for displaying launch status with consistent styling.
 * Used in admin launch list and review pages.
 */

import React from 'react';
import type { LaunchStatus } from '../../services/adminApi.js';

interface LaunchStatusBadgeProps {
  status: LaunchStatus;
  size?: 'sm' | 'md';
}

const STATUS_CONFIG: Record<LaunchStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-800' },
  pending_approval: { label: 'Pending Approval', className: 'bg-yellow-100 text-yellow-800' },
  pending_authorization: { label: 'Pending Auth', className: 'bg-blue-100 text-blue-800' },
  pending_payment: { label: 'Pending Payment', className: 'bg-orange-100 text-orange-800' },
  processing: { label: 'Processing', className: 'bg-indigo-100 text-indigo-800' },
  ready: { label: 'Ready', className: 'bg-cyan-100 text-cyan-800' },
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  paused: { label: 'Paused', className: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', className: 'bg-purple-100 text-purple-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
};

export function LaunchStatusBadge({ status, size = 'sm' }: LaunchStatusBadgeProps) {
  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm';

  return (
    <span className={`inline-flex rounded-full font-medium ${sizeClasses} ${config.className}`}>
      {config.label}
    </span>
  );
}
